import { useLocation, useRouter } from "@tanstack/react-router";
import type { ComponentProps, ComponentType } from "react";
import type { Entries } from "type-fest";

import { generateValueProps, translateLanguageCode } from "./common";

import FlagOfEngland from "~/shared/assets/flags/england.svg?react";
import FlagOfGermany from "~/shared/assets/flags/germany.svg?react";
import FlagOfRussia from "~/shared/assets/flags/russia.svg?react";
import FlagOfSlovakia from "~/shared/assets/flags/slovakia.svg?react";
import FlagOfUkraine from "~/shared/assets/flags/ukraine.svg?react";
import type { LanguageCode } from "~/shared/i18n";
import { SUPPORTED_LANGUAGES, useTranslation2 } from "~/shared/i18n";
import { Button } from "~/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "~/shared/ui/dropdown-menu";

type Props = ComponentProps<typeof DropdownMenu> & {
  triggerProps?:
    | Omit<ComponentProps<typeof DropdownMenuTrigger>, "asChild" | "children">
    | undefined;
};

function Dropdown({ triggerProps, ...props }: Props) {
  const {
    translation: { t, i18n },
    postproc: { tc },
  } = useTranslation2();
  const router = useRouter();
  const location = useLocation();

  return (
    <DropdownMenu {...props}>
      <DropdownMenuTrigger asChild {...triggerProps}>
        <Button variant="outline">{translateLanguageCode(t, i18n.language as LanguageCode)}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuRadioGroup {...generateValueProps(i18n, router, location)}>
          {(Object.entries(SUPPORTED_LANGUAGES) as Entries<typeof SUPPORTED_LANGUAGES>).map(
            ([key, value]) => {
              const FlagIcon = FLAG_ICONS_BY_LANGUAGE_CODE[key];
              return (
                <DropdownMenuRadioItem key={key} value={key} showIndicator={false}>
                  <FlagIcon />
                  {tc(value.toLocaleUpperCase(), {
                    lng: key,
                  })}
                </DropdownMenuRadioItem>
              );
            },
          )}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const FLAG_ICONS_BY_LANGUAGE_CODE: Record<LanguageCode, ComponentType> = {
  en: FlagOfEngland,
  uk: FlagOfUkraine,
  de: FlagOfGermany,
  sk: FlagOfSlovakia,
  ru: FlagOfRussia,
};

export { Dropdown };
